'use client';

import {
  ChevronUp,
  CreditCard,
  Languages,
  LogOut,
  SlidersHorizontal,
  User,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { isAdmin } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { useSession } from '@/hooks/use-session';
import { useLanguage } from '@/providers/i18n-provider';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LocaleLink } from '@/components/locale-link';
import { getInitials, resolveAvatarSrc } from './avatar-utils';
import { SHELL_FOCUS } from './SidebarNav';

export function useShellPlan() {
  const { data: session, status } = useSession();
  const role = session?.user.role;
  const pro = role === 'Pro' || role === 'Administrator' || isAdmin(session);
  return { pro, free: status === 'authenticated' && !pro };
}

const ACCOUNT_LINKS = [
  { key: 'accountSettings', path: '/account', icon: User },
  { key: 'billing', path: '/account/billing', icon: CreditCard },
  {
    key: 'notificationSettings',
    path: '/account#notifications',
    icon: SlidersHorizontal,
  },
] as const;

export function SidebarAccountMenu({
  rail = false,
  mobile = false,
  onNavigate,
}: {
  rail?: boolean;
  mobile?: boolean;
  onNavigate?: () => void;
}) {
  const { data: session } = useSession();
  const { pro } = useShellPlan();
  const { t } = useTranslation();
  const { languageCode, changeLanguage } = useLanguage();
  const name =
    session?.user.name ||
    session?.user.email ||
    t('navigation.accountSettings');
  const flag = (
    <img
      src={
        languageCode === 'en'
          ? '/media/flags/united-kingdom.svg'
          : '/media/flags/germany.svg'
      }
      alt=""
      className="h-3.5 w-5 rounded-[2px] object-cover"
    />
  );
  const languageLabel = (
    <>
      <Languages aria-hidden className="size-4 shrink-0" />
      <span className="min-w-0 grow break-words">{t('navigation.language')}</span>
      {flag}
      <span className="fd-mono text-xs">{languageCode.toUpperCase()}</span>
    </>
  );
  const toggleLanguage = () => {
    void changeLanguage(languageCode === 'en' ? 'de' : 'en');
    onNavigate?.();
  };
  const accountRow = (
    <>
      <Avatar className="size-8 shrink-0">
        <AvatarImage src={resolveAvatarSrc(session?.user.avatar)} alt="" />
        <AvatarFallback>
          {getInitials(session?.user.name, session?.user.email)}
        </AvatarFallback>
      </Avatar>
      {!rail && (
        <>
          <span className="flex min-w-0 grow flex-col items-start gap-1">
            <span className="w-full truncate text-sm font-medium">{name}</span>
            <Badge
              size="xs"
              variant="outline"
              className="border-[var(--fd-line-2)] text-[var(--fd-muted)]"
            >
              {t(pro ? 'navigation.planPro' : 'navigation.planFree')}
            </Badge>
          </span>
          <ChevronUp className="size-4 shrink-0" aria-hidden />
        </>
      )}
    </>
  );
  const rowClass = cn(
    'flex min-h-11 w-full min-w-0 items-center gap-2 rounded-md px-3 text-sm text-[var(--fd-ink)] hover:bg-[var(--fd-bg)]',
    SHELL_FOCUS,
  );
  const triggerClass = cn(
    'flex min-h-11 w-full items-center gap-2 rounded-md p-2 text-left hover:bg-[var(--fd-bg)]',
    rail && 'justify-center px-0',
    SHELL_FOCUS,
  );
  if (mobile)
    return (
      <Collapsible>
        <CollapsibleTrigger
          aria-label={t('navigation.accountSettings')}
          className={triggerClass}
        >
          {accountRow}
        </CollapsibleTrigger>
        <CollapsibleContent className="max-h-[40dvh] overflow-y-auto">
          {ACCOUNT_LINKS.map(({ key, path, icon: Icon }) => (
            <LocaleLink
              key={key}
              href={path}
              className={rowClass}
              onClick={onNavigate}
            >
              <Icon className="size-4 shrink-0" aria-hidden />
              <span className="min-w-0 flex-1 break-words">{t(`navigation.${key}`)}</span>
            </LocaleLink>
          ))}
          <div className="my-1 border-t border-[var(--fd-line)]" />
          <button type="button" onClick={toggleLanguage} className={rowClass}>
            {languageLabel}
          </button>
          <div className="my-1 border-t border-[var(--fd-line)]" />
          <button
            type="button"
            onClick={() => {
              window.location.href = '/auth/logout';
            }}
            className={rowClass}
          >
            <LogOut className="size-4 shrink-0" aria-hidden />
            <span className="min-w-0 flex-1 break-words">{t('navigation.logout')}</span>
          </button>
        </CollapsibleContent>
      </Collapsible>
    );
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        aria-label={t('navigation.accountSettings')}
        className={triggerClass}
      >
        {accountRow}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side={rail ? 'right' : 'top'}
        align={rail ? 'end' : 'start'}
        sideOffset={8}
        collisionPadding={12}
        className="fd-header w-72 border-[var(--fd-line)]"
      >
        <div className="px-3 py-2">
          <p className="truncate text-sm font-semibold">{name}</p>
          <p className="truncate text-xs text-[var(--fd-muted)]">
            {session?.user.email}
          </p>
        </div>
        <DropdownMenuSeparator />
        {ACCOUNT_LINKS.map(({ key, path, icon: Icon }) => (
          <DropdownMenuItem key={key} asChild>
            <LocaleLink href={path} className={rowClass} onClick={onNavigate}>
              <Icon aria-hidden />
              <span className="min-w-0 flex-1 break-words">{t(`navigation.${key}`)}</span>
            </LocaleLink>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem className={rowClass} onSelect={toggleLanguage}>
          {languageLabel}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className={rowClass}
          onSelect={() => {
            window.location.href = '/auth/logout';
          }}
        >
          <LogOut aria-hidden />
          <span className="min-w-0 flex-1 break-words">{t('navigation.logout')}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
